/**
 * /portal/mi-acceso — ver `MEMBERSHIP_ROUTE.MI_ACCESO`
 *
 * Canónico bajo el portal: el fan se queda en /portal/* con el link asignado.
 * Legacy /mi-acceso redirige acá.
 *
 * Solo muestra el link de acceso privado (Telegram) + acciones:
 *   - Abrir link
 *   - Copiar
 *   - Enviar email con el link (self-service; admin también puede desde /admin/memberships)
 *
 * Not approved → portal. No session → registro.
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import {
  isClerkAdmin,
  isProfileComplete,
  useClerkSessionClaims,
  useTelegramInviteUrl,
} from "@/lib/auth";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE, USER_PORTAL_PATH } from "@/lib/membershipAccessFlow";
import { useClerk, useSession, useUser } from "@clerk/clerk-react";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  Send,
} from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";

const SEO = {
  title: `Tu acceso — ${BRAND_PERSON_NAME}`,
  description: `Link privado de acceso a la membresía de ${BRAND_PERSON_NAME}.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.MI_ACCESO}`,
  robots: "noindex, nofollow" as const,
};

export default function MiAcceso() {
  useSEO(SEO);
  const { user, isLoaded } = useUser();
  const { session } = useSession();
  const { signOut } = useClerk();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const { claims } = useClerkSessionClaims();
  const telegramFromClaims = useTelegramInviteUrl();

  if (!isLoaded) {
    return (
      <div
        className="fixed inset-0 z-[100] flex min-h-svh items-center justify-center bg-black"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Loader2 className="h-8 w-8 animate-spin text-white/40" aria-hidden />
      </div>
    );
  }

  if (!user) return <Navigate to={MEMBERSHIP_ROUTE.REGISTRO} replace />;
  if (isClerkAdmin(user)) return <Navigate to="/admin" replace />;

  // Sin perfil completo no hay acceso al link — volver a completar perfil.
  // (isProfileComplete imported with claims helpers)
  if (!isProfileComplete(user)) {
    return <Navigate to={MEMBERSHIP_ROUTE.PORTAL_COMPLETAR_REGISTRO} replace />;
  }

  const approved =
    claims.approved === true || user.publicMetadata?.approved === true;
  // Sin link asignado todavía: el portal muestra el estado sin bloquear.
  if (!approved) return <Navigate to={USER_PORTAL_PATH} replace />;

  const accessUrl =
    telegramFromClaims ??
    ((user.publicMetadata?.telegram_invite_url as string | undefined) ?? null);
  const email = user.primaryEmailAddress?.emailAddress ?? "";

  const copy = async () => {
    if (!accessUrl) return;
    try {
      await navigator.clipboard.writeText(accessUrl);
      setCopied(true);
      toast({ title: "Link copiado" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  /** Self-service: reenvía el email con el mismo link de acceso. */
  const emailMeTheLink = async () => {
    if (!session || emailing) return;
    setEmailing(true);
    try {
      const token = await session.getToken();
      if (!token) {
        toast({
          title: "Sesión expirada",
          description: "Iniciá sesión de nuevo.",
          variant: "destructive",
        });
        return;
      }
      const res = await fetch("/api/membership/resend-invitation", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) {
        toast({
          title: "Email enviado",
          description: email
            ? `Te mandamos el link a ${email}. Revisá bandeja y spam.`
            : "Revisá tu bandeja de entrada y spam.",
        });
      } else if (res.status === 429) {
        toast({
          title: "Ya te lo enviamos hace poco",
          description: data.error ?? "Esperá unas horas antes de pedir otro email.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "No se pudo enviar el email",
          description: data.error ?? `Error ${res.status}`,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error de red",
        description: "Probá de nuevo en un momento.",
        variant: "destructive",
      });
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4 py-8">
      <Card className="max-w-md w-full border-white/10 bg-zinc-900/80 backdrop-blur shadow-2xl shadow-black/40">
        <CardHeader className="text-center space-y-2 pb-2">
          <CardTitle className="text-xl sm:text-2xl text-zinc-100">Tu acceso</CardTitle>
          <CardDescription className="text-sm text-zinc-400">
            Link privado de tu membresía con {BRAND_PERSON_NAME}.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {accessUrl ? (
            <>
              <div className="rounded-xl bg-[#0088cc]/10 border border-[#0088cc]/30 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#0088cc] shrink-0" />
                  <span className="font-semibold text-zinc-100 text-sm">Link de acceso</span>
                </div>
                <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2.5 border border-white/5">
                  <code className="flex-1 text-xs text-zinc-300 font-mono truncate" title={accessUrl}>
                    {accessUrl}
                  </code>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => void copy()}
                    className="text-zinc-400 hover:text-zinc-100 hover:bg-white/10 shrink-0"
                    aria-label="Copiar link"
                  >
                    {copied ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                asChild
                className="w-full h-12 bg-[#0088cc] hover:bg-[#0099d8] text-white font-semibold gap-2 text-base"
              >
                <a href={accessUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" />
                  Abrir link de acceso
                  <ExternalLink className="w-4 h-4 opacity-80" />
                </a>
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full h-11 border-white/15 bg-white/5 text-zinc-100 hover:bg-white/10 gap-2"
                onClick={() => void emailMeTheLink()}
                disabled={emailing || !session}
              >
                {emailing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Enviarme el link por email
              </Button>

              <p className="text-[11px] text-amber-200/80 leading-relaxed text-center">
                Este link es personal. No lo compartas.
              </p>
            </>
          ) : (
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-4 text-sm text-rose-200 space-y-2">
              <p>
                Estás aprobado, pero todavía no hay link de acceso cargado. Esperá unos minutos o
                escribinos.
              </p>
              <a
                href={`mailto:${BRAND_SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-1.5 underline underline-offset-2 text-rose-100"
              >
                <Mail className="w-3.5 h-3.5" />
                {BRAND_SUPPORT_EMAIL}
              </a>
            </div>
          )}

          <div className="pt-2 border-t border-white/5 flex items-center justify-between text-xs">
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1.5"
            >
              <Mail className="w-3 h-3" />
              Soporte
            </a>
            <button
              type="button"
              onClick={() => void signOut()}
              className="text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1.5"
            >
              <LogOut className="w-3 h-3" />
              Salir
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
